import   React      ,
       { useState   ,
         useEffect  ,
         useRef     }  from "react";
import   classNames    from "classnames";
import   styles        from "./tab.module.scss";
import { TriggerSet }  from "../actuators/TriggerSet";

/** Demande d'afficher une section : `window.dispatchEvent(new CustomEvent(ACTIVATE_PANEL_EVENT, { detail: { panelKey } }))`. */
export const ACTIVATE_PANEL_EVENT = 'regie:activate-panel';

/** Garde posée par une section qui ne doit pas être quittée sans confirmation (2026-10-20 :
 *  création de chaîne non enregistrée). Elle reçoit le changement de section à exécuter et
 *  rend true si elle le retient — à elle de l'appeler plus tard si l'utilisateur confirme. */
type PanelLeaveGuard = (proceed: () => void) => boolean;
let panelLeaveGuard: PanelLeaveGuard | null = null;
export const setPanelLeaveGuard = (guard: PanelLeaveGuard | null) => { panelLeaveGuard = guard; };

type SubChoice = {
  label       : string;
  panelKey    : string;
  component?  : JSX.Element;
  keepMounted?: boolean;
};

type MainChoice = {
  title       : string;
  panelKey?   : string;
  subChoices? : SubChoice[];
  component?  : JSX.Element;
  keepMounted?: boolean;
  /** Pastille rouge sur le bouton de l'onglet (« quelque chose attend ici »). */
  badge?      : boolean;
  /** Appelé à chaque clic sur ce bouton d'onglet (qu'il soit déjà actif ou non) —
   *  utile pour un panneau keepMounted qui doit revenir à sa vue par défaut. */
  onActivate? : () => void;
};

type TabProps = {
  mainChoices                     : MainChoice[];
  defaultActivePanel?             : string;
  classNameNormalButton?          : string;
  classNamePushedButton?          : string;
  position?                       : "top" | "bottom" | "left" | "right";
  variant?                        : "default" | "tailwind1" | "tailwind2" | "custom";
  width?                          : string;
  height?                         : string;
  padding?                        : string;
  margin?                         : string;
  buttonForegroundColor?          : string;
  buttonBackgroundColor?          : string;
  buttonBackgroundHoverColor?     : string;
  buttonForegroundHoverColor?     : string;
  buttonBackgroundSelectedColor?  : string;
  buttonForegroundSelectedColor?  : string;
  buttonBackgroundDisabledColor?  : string;
  buttonForegroundDisabledColor?  : string;
  headerContent?                  : React.ReactNode;
  /** Bureau V2 : le menu passe au-dessus du contenu, le logo reste en haut à gauche. */
  topNav?                         : boolean;
  /** Avec `topNav` : panelKey des onglets rejetés à droite de la barre (Paramètres, Panic…). */
  rightPanelKeys?                 : string[];
  /** Avec `topNav` : contenu posé sous les panneaux, en pied de page (liens légaux de la régie). */
  footerContent?                  : React.ReactNode;
};

export const Tab = ({ mainChoices                                           ,
                      defaultActivePanel                                    ,
                      classNameNormalButton                                 ,
                      classNamePushedButton                                 ,
                      position                           = "left"          ,
                      variant                            = "default"       ,
                      width                                                 ,
                      height                                                ,
                      margin                                                ,
                      padding                                               ,
                      buttonForegroundColor                                 ,
                      buttonBackgroundColor                                 ,
                      buttonBackgroundHoverColor                            ,
                      buttonForegroundHoverColor                            ,
                      buttonBackgroundSelectedColor                         ,
                      buttonForegroundSelectedColor                         ,
                      buttonBackgroundDisabledColor                         ,
                      buttonForegroundDisabledColor                         ,
                      headerContent                                         ,
                      topNav                             = false           ,
                      rightPanelKeys                                        ,
                      footerContent                                         ,
                    }: TabProps) => {

  const [ activePanel  , setActivePanel  ] = useState<string | null>(
    defaultActivePanel
    ?? mainChoices[0]?.panelKey
    ?? mainChoices[0]?.subChoices?.[0]?.panelKey
    ?? null
  );
  const [ choices      , setChoices      ] = useState<string[]> ( () => mainChoices.map((choice) => choice.title) );
  const [ version      , setVersion      ] = useState(0);
  const [ menuVisible  , setMenuVisible  ] = useState(true);
  // TriggerSet garde son propre bouton enfoncé : on le remonte quand le changement de section
  // est retenu par la garde, sinon le menu montrerait une section qui n'est pas affichée.
  const [ menuResetKey , setMenuResetKey ] = useState(0);
  const   sidebarRef                       = useRef<HTMLDivElement>(null);
  const [ sidebarWidth , setSidebarWidth ] = useState<number | undefined>(undefined);

  const isVertical = !topNav && (position === "left" || position === "right");

  const hasColorProps = buttonForegroundColor         !== undefined ||
                        buttonBackgroundColor         !== undefined ||
                        buttonBackgroundHoverColor    !== undefined ||
                        buttonForegroundHoverColor    !== undefined ||
                        buttonBackgroundSelectedColor !== undefined ||
                        buttonForegroundSelectedColor !== undefined ||
                        buttonBackgroundDisabledColor !== undefined ||
                        buttonForegroundDisabledColor !== undefined;

  const hasCustomClasses = classNameNormalButton !== undefined ||
                           classNamePushedButton !== undefined;

  const effectiveVariant = hasColorProps    ? variant  :
                           hasCustomClasses ? "custom" :
                                              variant  ;

  const effectiveClassNameNormal = effectiveVariant === "custom"
    ? classNames(styles.triggerNormal, classNameNormalButton)
    : classNameNormalButton;

  const effectiveClassNamePushed = effectiveVariant === "custom"
    ? classNames(styles.triggerPushed, classNamePushedButton)
    : classNamePushedButton;

  useEffect(() => {
    setChoices(mainChoices.map((choice) => choice.title));
    if (!mainChoices.some((choice) => choice.panelKey === activePanel)) {
      setActivePanel(
        defaultActivePanel
        ?? mainChoices[0]?.panelKey
        ?? mainChoices[0]?.subChoices?.[0]?.panelKey
        ?? null
      );
    }
  }, [mainChoices, version]);

  // Capture la largeur naturelle de la sidebar au premier rendu
  useEffect(() => {
    if (sidebarRef.current && sidebarWidth === undefined && choices.length > 0) {
      setSidebarWidth(sidebarRef.current.scrollWidth);
    }
  }, [choices]);

  const handleToggleMenu = () => {
    if (sidebarRef.current && sidebarWidth === undefined) {
      setSidebarWidth(sidebarRef.current.scrollWidth);
    }
    setMenuVisible(v => !v);
  };

  const handleChoiceChange = (index: number) => {
    const choice = mainChoices[index];
    if (choice.panelKey !== activePanel && panelLeaveGuard?.(() => applyChoiceChange(index))) {
      setMenuResetKey(k => k + 1);
      return;
    }
    applyChoiceChange(index);
  };

  const applyChoiceChange = (index: number) => {
    const choice = mainChoices[index];
    if (choice.panelKey !== activePanel) {
      setActivePanel(choice.panelKey ?? null);
    }
    choice.onActivate?.();
    setVersion(version + 1);
  };

  // Changer de section depuis le contenu lui-même (2026-10-17 : boutons « Régie » et
  // « Fiche » du Dashboard, cf. desktop.tsx) : `regie:activate-panel` { panelKey }.
  useEffect(() => {
    const onActivate = (e: Event) => {
      const panelKey = (e as CustomEvent<{ panelKey: string }>).detail?.panelKey;
      if (panelKey && mainChoices.some(choice => choice.panelKey === panelKey)) setActivePanel(panelKey);
    };
    window.addEventListener(ACTIVATE_PANEL_EVENT, onActivate);
    return () => window.removeEventListener(ACTIVATE_PANEL_EVENT, onActivate);
  }, [mainChoices]);

  const renderTriggerSet = ( triggerChoices : string[]            ,
                             activedState   : boolean[]           ,
                             onChoiceChange : (i: number) => void ,
                             badges?        : boolean[]           ) => (
    <TriggerSet choices                       = {triggerChoices}
                classNameNormal               = {effectiveClassNameNormal}
                classNamePushed               = {effectiveClassNamePushed}
                onChoiceChange                = {onChoiceChange}
                exclusive
                orientation                   = {isVertical ? "vertical" : "horizontal"}
                variant                       = {effectiveVariant}
                width                         = {width}
                height                        = {height}
                margin                        = {margin}
                padding                       = {padding}
                actived                       = {activedState}
                badges                        = {badges}
                buttonForegroundColor         = {buttonForegroundColor}
                buttonBackgroundColor         = {buttonBackgroundColor}
                buttonBackgroundHoverColor    = {buttonBackgroundHoverColor}
                buttonForegroundHoverColor    = {buttonForegroundHoverColor}
                buttonBackgroundSelectedColor = {buttonBackgroundSelectedColor}
                buttonForegroundSelectedColor = {buttonForegroundSelectedColor}
                buttonBackgroundDisabledColor = {buttonBackgroundDisabledColor}
                buttonForegroundDisabledColor = {buttonForegroundDisabledColor}
    />
  );

  const contentPanels = (
        <div className={classNames(styles.content)}>
          {mainChoices.map((choice, index) => {

            const panelKey = choice.panelKey;
            const isActive = activePanel === panelKey || (!activePanel && mainChoices[index]?.panelKey === panelKey);

            if (choice.keepMounted) {
              return (
                <div key={panelKey ?? index} style={{ display: isActive ? undefined : 'none' }}>
                  {choice.component}
                  {choice.subChoices && choice.subChoices.map((subChoice, subIndex) => {
                    const subActive = activePanel === subChoice.panelKey;
                    if (subChoice.keepMounted) {
                      return (
                        <div key={subIndex} style={{ display: subActive ? undefined : 'none' }}>
                          { renderTriggerSet([subChoice.label], [subActive], () => setActivePanel(subChoice.panelKey)) }
                          {subChoice.component && <div>{subChoice.component}</div>}
                        </div>
                      );
                    }
                    return subActive ? (
                      <div key={subIndex}>
                        { renderTriggerSet([subChoice.label], [subActive], () => setActivePanel(subChoice.panelKey)) }
                        {subChoice.component && <div>{subChoice.component}</div>}
                      </div>
                    ) : null;
                  })}
                </div>
              );
            }

            return (
              isActive && (
                <div key={`${index}-${version}`}>
                  {choice.component}
                  {choice.subChoices && choice.subChoices.map((subChoice, subIndex) => (
                    <div key={subIndex}>
                      { renderTriggerSet(
                          [subChoice.label],
                          [activePanel === subChoice.panelKey],
                          () => setActivePanel(subChoice.panelKey)
                        )
                      }
                      {subChoice.component && <div>{subChoice.component}</div>}
                    </div>
                  ))}
                </div>
              )
            );
          })}
        </div>
  );

  if (topNav) {
    return (
      <div className={styles.topRoot}>
        <div className={styles.topBar}>
          <img src="/images/logo.png" alt="logo" className={styles.topLogo} />
          <div className={styles.topMenu}>
            { [false, true].map(right => {
                const idx = mainChoices
                  .map((choice, index) => index)
                  .filter(index => !!rightPanelKeys?.includes(mainChoices[index].panelKey ?? '') === right);
                if (idx.length === 0) return null;
                const activeIndex = mainChoices.findIndex((choice) => choice.panelKey === activePanel);
                return (
                  <div key={`${right}-${activePanel}-${menuResetKey}`} className={right ? styles.topMenuRight : undefined}>
                    { renderTriggerSet(
                        idx.map(index => mainChoices[index].title),
                        idx.map(index => index === activeIndex),
                        (i: number) => handleChoiceChange(idx[i]),
                        idx.map(index => !!mainChoices[index].badge)
                      )
                    }
                  </div>
                );
              })
            }
          </div>
          <div className={styles.topHeader}>
            {headerContent}
          </div>
        </div>
        {contentPanels}
        {footerContent}
      </div>
    );
  }

  return (
    <div className={classNames(styles.root, styles[position])}>

      {/* ── Zone gauche : poignée + sidebar ────────────────────────── */}
      <div className={styles.leftZone}>

        {/* Poignée cliquable dans la marge gauche de 10px de .root */}
        <div
          className={classNames(styles.toggleHandle, { [styles.collapsed]: !menuVisible })}
          onClick={handleToggleMenu}
          title={menuVisible ? "Masquer le menu" : "Afficher le menu"}
        >
          <svg viewBox="0 0 6 10" xmlns="http://www.w3.org/2000/svg">
            <polyline
              points="5,1 1,5 5,9"
              stroke="white"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </div>

        {/* Sidebar animée : logo + menu, se referme entièrement */}
        <div
          ref={sidebarRef}
          className={classNames(styles.sidebar, { [styles.sidebarHidden]: !menuVisible })}
          style={sidebarWidth !== undefined ? { width: menuVisible ? sidebarWidth : 0 } : undefined}
        >
          <img src="/images/logo.png" alt="logo" className={styles.logo} />
          <div key={`${activePanel}-${menuResetKey}`} className={classNames(styles.menu)}>
            { renderTriggerSet(
                choices,
                choices.map((_, index) => index === mainChoices.findIndex((choice) => choice.panelKey === activePanel)),
                handleChoiceChange,
                mainChoices.map((choice) => !!choice.badge)
              )
            }
          </div>
        </div>

      </div>

      {/* ── Zone principale ─────────────────────────────────────────── */}
      <div className={styles.mainArea}>
        <div className={styles.header}>
          {headerContent}
        </div>

        {contentPanels}
      </div>

    </div>
  );
};

export default Tab;